// Shared analysis views: pattern of life, incident window, IP analysis (with
// likely home/work and VPN/Tor screening), contact ranking, links between
// accounts, account/security events, and a case-summary PDF builder. Every view
// is marked as generated by LE Tech Tools. Numbers come from utils/analysisKit.js.

import React, { useState, useMemo, useEffect } from 'react';
import {
  useT, DARK, NavBtn, InfoCard, InfoRow, Heading, Note, Explain, Chip, DataTable, GeneratedBadge, GENERATED,
  btnStyle, selectStyle, plural, useOnlineGate,
} from './ui';
import {
  patternOfLife, activityGaps, incidentWindow, zonedToEpoch, ipProfile, homeAndWork, ipv6Networks,
} from '../../utils/analysisKit';
import { useIpDatasets, lookupLocal } from '../../utils/ipIntelDatasets';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const ORDER = [1, 2, 3, 4, 5, 6, 0]; // Monday first

// Days from first to last, including days with nothing, so gaps show as empty bars.
function fillDays(days) {
  if (!days.length) return [];
  const byDay = new Map(days.map(d => [d.day, d.count]));
  const out = [];
  for (let t = Date.parse(days[0].day + 'T00:00:00Z'), end = Date.parse(days[days.length - 1].day + 'T00:00:00Z'); t <= end; t += 86400000) {
    const day = new Date(t).toISOString().slice(0, 10);
    out.push({ day, count: byDay.get(day) || 0 });
  }
  return out;
}

const scheme = t => (t === DARK ? 'dark' : 'light');
export const hoursText = h => (h >= 48 ? `${(h / 24).toFixed(1)} days` : `${h.toFixed(1)} hours`);

// ─── Pattern of life ─────────────────────────────────────────────────────────

export function PatternOfLifeView({ events, tz, fmt, tzLabel, explain }) {
  const t = useT();
  const [source, setSource] = useState('all');
  const [gapHours, setGapHours] = useState(12);
  const activity = useMemo(() => events.filter(e => e.activity), [events]);
  const sources = useMemo(() => [...new Set(activity.map(e => e.source))].sort(), [activity]);
  const shown = useMemo(() => (source === 'all' ? activity : activity.filter(e => e.source === source)), [activity, source]);
  const pol = useMemo(() => patternOfLife(shown, tz), [shown, tz]);
  const gaps = useMemo(() => activityGaps(shown, gapHours), [shown, gapHours]);
  const days = useMemo(() => fillDays(pol.days), [pol]);
  const maxDay = Math.max(1, ...days.map(d => d.count));
  const byHour = useMemo(() => {
    const out = new Array(24).fill(0);
    pol.grid.forEach(row => row.forEach((n, h) => { out[h] += n; }));
    return out;
  }, [pol]);
  const sel = selectStyle(t);

  return (
    <>
      <Heading title="Pattern of life" sub={`When the account is active, by day of week and hour (${tzLabel}).`}>
        <select value={source} onChange={e => setSource(e.target.value)} style={sel}>
          <option value="all">All activity</option>
          {sources.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </Heading>
      <GeneratedBadge />
      <Explain>
        {explain || 'Each square counts activity records in that hour of that weekday, in the time zone picked at the top. Darker means busier.'}
        {' '}The hours that stay empty every day are usually when the person sleeps; a weekday-only block can point to work
        hours. Phones also log activity on their own, so treat this as a rhythm, not proof of what the person was doing.
      </Explain>
      {!pol.total ? <Note>No activity records with times for this selection.</Note> : (
        <>
          <div style={{ overflowX: 'auto', flexShrink: 0 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '44px repeat(24, minmax(22px, 1fr))', gap: 2, minWidth: 620 }}>
              <div />
              {Array.from({ length: 24 }, (_, h) => (
                <div key={h} style={{ fontSize: 10, color: t.muted, textAlign: 'center' }}>{h % 3 === 0 ? String(h).padStart(2, '0') : ''}</div>
              ))}
              {ORDER.map(wd => (
                <React.Fragment key={wd}>
                  <div style={{ fontSize: 11.5, color: t.muted, alignSelf: 'center' }}>{DAYS[wd]}</div>
                  {pol.grid[wd].map((n, h) => (
                    <div key={h} title={`${DAYS[wd]} ${String(h).padStart(2, '0')}:00–${String(h).padStart(2, '0')}:59 · ${n.toLocaleString()} record${n === 1 ? '' : 's'}`}
                      style={{ height: 24, borderRadius: 3, background: n ? `rgba(59,130,246,${0.15 + 0.85 * (n / pol.max)})` : t.heatEmpty }} />
                  ))}
                </React.Fragment>
              ))}
              <div style={{ fontSize: 10.5, color: t.muted, alignSelf: 'center' }}>All</div>
              {byHour.map((n, h) => (
                <div key={h} title={`${String(h).padStart(2, '0')}:00 all days · ${n.toLocaleString()}`} style={{ fontSize: 9.5, color: n ? t.muted : t.warn, textAlign: 'center' }}>{n || '0'}</div>
              ))}
            </div>
          </div>

          <InfoCard title={`Activity per day (${plural(days.length, 'day')})`}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 90, overflowX: 'auto' }}>
              {days.map(d => (
                <div key={d.day} title={`${d.day} · ${d.count.toLocaleString()}`}
                  style={{ flex: '1 0 4px', minWidth: 4, height: d.count ? Math.max(3, Math.round(d.count / maxDay * 90)) : 2, background: d.count ? t.accent : t.warn, opacity: d.count ? 1 : 0.6, borderRadius: 1 }} />
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: t.muted, marginTop: 6 }}>
              <span>{days[0]?.day}</span><span>Amber = no activity that day</span><span>{days[days.length - 1]?.day}</span>
            </div>
          </InfoCard>

          <Heading title="Activity gaps" sub="Stretches with no activity at all. They can mean the phone was off, in airplane mode, out of service, or replaced.">
            <label style={{ fontSize: 12, color: t.muted }}>Longer than{' '}
              <select value={gapHours} onChange={e => setGapHours(Number(e.target.value))} style={sel}>
                {[4, 8, 12, 24, 48, 72].map(h => <option key={h} value={h}>{h} hours</option>)}
              </select>
            </label>
          </Heading>
          <DataTable key={tz + gapHours + source} rows={gaps} exportName="activity gaps" initialSort={{ key: 'hours', dir: 'desc' }}
            emptyText={`No gaps longer than ${gapHours} hours.`}
            columns={[
              { key: 'from', label: `Last activity before (${tzLabel})`, mono: true, text: r => fmt(r.from), sort: r => r.from },
              { key: 'to', label: 'Activity resumes', mono: true, text: r => fmt(r.to), sort: r => r.to },
              { key: 'hours', label: 'Length', text: r => hoursText(r.hours), sort: r => r.hours },
              { key: 'before', label: 'Last record was', text: r => r.before },
              { key: 'after', label: 'First record after', text: r => r.after },
            ]} />
        </>
      )}
    </>
  );
}

// ─── Incident window ─────────────────────────────────────────────────────────

export function IncidentView({ events, tz, fmt, tzLabel, accountLabel = a => a, homeWork = {}, handleLabel = 'Contacts', placeLabel = 'Cell sites', explain, modelName, show = h => h }) {
  const t = useT();
  const [when, setWhen] = useState('');
  const [hours, setHours] = useState(3);
  const center = when ? zonedToEpoch(when, tz) : null;
  const w = useMemo(() => (center != null ? incidentWindow(events, center, hours) : null), [events, center, hours]);
  const tag = ip => {
    const tags = [];
    if (homeWork.home?.ips.includes(ip)) tags.push(<Chip key="h" tone="good">likely home</Chip>);
    if (homeWork.work?.ips.includes(ip)) tags.push(<Chip key="w" tone="good">likely work</Chip>);
    return tags;
  };
  const list = (items, mono, render) => (items.length ? items.slice(0, 15).map(([k, n], i) => (
    <InfoRow key={k} first={i === 0} mono={mono} label={<span>{render ? render(k) : k}</span>} value={`${n}×`} />
  )) : <div style={{ fontSize: 13, color: t.muted }}>None.</div>);
  const sel = selectStyle(t);

  return (
    <>
      <Heading title="Incident window" sub="Everything around a moment you choose." />
      <GeneratedBadge />
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', fontSize: 13, color: t.muted }}>
        <label>Date and time ({tzLabel}){' '}
          <input type="datetime-local" value={when} onChange={e => setWhen(e.target.value)} style={{ ...sel, colorScheme: scheme(t) }} />
        </label>
        <label>plus or minus{' '}
          <select value={hours} onChange={e => setHours(Number(e.target.value))} style={sel}>
            {[0.5, 1, 2, 3, 6, 12, 24, 48].map(h => <option key={h} value={h}>{h < 1 ? '30 minutes' : plural(h, 'hour')}</option>)}
          </select>
        </label>
      </div>
      <Explain>
        {explain || 'Pick the time of the incident and how wide a window to look at.'} You get every dated record in that window,
        the IP addresses (tagged when they match the likely home or work network), who was in contact, and the last activity
        just before and just after the window. A quiet window next to busy hours is worth noting too.
      </Explain>
      {!w ? <Note>Choose a date and time to see the window.</Note> : (
        <>
          <div style={{ fontSize: 13, color: t.text }}>
            {fmt(w.from)} to {fmt(w.to)}: <strong>{plural(w.events.length, 'record')}</strong>
            {w.accounts.length > 0 && <> from {w.accounts.map(([a]) => accountLabel(a)).join(', ')}</>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
            {w.ips.length > 0 && <InfoCard title="IP addresses in the window">{list(w.ips, true, ip => <>{ip} {tag(ip)}</>)}</InfoCard>}
            <InfoCard title={handleLabel}>{list(w.handles, true, show)}</InfoCard>
            {w.places.length > 0 && <InfoCard title={placeLabel}>{list(w.places, true)}</InfoCard>}
            <InfoCard title="What was used">{list(w.sources)}</InfoCard>
            <InfoCard title="Just outside the window">
              <InfoRow first label="Last activity before" value={w.before ? `${fmt(w.before.t)} · ${w.before.source}` : 'None in the records'} />
              <InfoRow label="First activity after" value={w.after ? `${fmt(w.after.t)} · ${w.after.source}` : 'None in the records'} />
              {w.models.length > 0 && <InfoRow label="Devices" value={w.models.map(([m]) => `${m}${modelName && modelName(m) ? ` (${modelName(m)})` : ''}`).join(', ')} />}
            </InfoCard>
          </div>
          <DataTable key={String(center) + hours + tz} rows={w.events} exportName="incident window" initialSort={{ key: 't', dir: 'asc' }}
            emptyText="No records in this window."
            columns={[
              { key: 't', label: `Time (${tzLabel})`, mono: true, text: r => fmt(r.t), sort: r => r.t, width: 190 },
              { key: 'account', label: 'Account', text: r => accountLabel(r.account) },
              { key: 'source', label: 'Source' },
              { key: 'detail', label: 'Details', maxWidth: 700, minWidth: 340 },
            ]} />
        </>
      )}
    </>
  );
}

// ─── IP analysis ─────────────────────────────────────────────────────────────

export const screeningFlagged = r => !!(r && (r.isTor || r.isAppleRelay || r.isVpn || r.isDatacenter || r.threatListCount > 0));

export function screeningLabel(r) {
  if (!r) return '';
  const flags = [];
  if (r.isTor) flags.push('Tor exit');
  if (r.isAppleRelay) flags.push('iCloud Private Relay');
  if (r.isVpn) flags.push('VPN');
  if (r.isDatacenter) flags.push(r.cloudProvider ? `Hosting (${r.cloudProvider})` : 'Hosting / datacenter');
  if (r.threatListCount > 0) flags.push(`On ${r.threatListCount} abuse list${r.threatListCount === 1 ? '' : 's'}`);
  if (flags.length) return flags.join(', ');
  return r.ipv4Supported ? 'No flags' : 'IPv6: only Tor and Private Relay checked';
}

// Loads the IP tool's blocklists (its saved copies, or a fresh download of the
// public lists) only once mounted, and screens the IPs in the browser.
export function IpScreening({ ips, onResults }) {
  const t = useT();
  const ds = useIpDatasets();
  const busy = ds.status === 'loading' || ds.status === 'refreshing' || !ds.complete;
  // Re-screen only when the lists or the set of IPs actually change.
  const ipKey = ips.join('|');
  useEffect(() => {
    if (!ds.ready) return;
    const out = {};
    ipKey.split('|').filter(Boolean).forEach(ip => { out[ip.toLowerCase()] = lookupLocal(ip, ds.indexes); });
    onResults(out, !busy);
  }, [ds.indexes, ds.ready, busy, ipKey, onResults]);
  return (
    <span style={{ fontSize: 12, color: busy ? t.warn : t.good }}>
      {busy ? 'Loading VPN / Tor / hosting lists…' : `Screened ${plural(ips.length, 'IP')} against the IP tool's lists.`}
    </span>
  );
}

export function HomeWorkCards({ homeWork, fmt, profile, noun = 'network', groupNote = 'addresses on this network' }) {
  const t = useT();
  const card = (title, g, what) => (
    <InfoCard title={<>{title}<span title={GENERATED} style={{ color: t.warn }}> ✱</span></>}>
      {!g ? <div style={{ fontSize: 13, color: t.muted }}>Nothing was seen on 3 or more {what}.</div> : (
        <>
          <div style={{ fontFamily: 'monospace', color: t.cyan, fontSize: 14, wordBreak: 'break-all' }}>{g.label}</div>
          <div style={{ fontSize: 12.5, color: t.text, marginTop: 6, lineHeight: 1.6 }}>
            Seen on <strong>{g.nights}</strong> different nights (22:00–06:00) and <strong>{g.workdays}</strong> weekdays during
            working hours (09:00–17:00), {plural(g.count, 'record')} in all{g.ips.length > 1 ? `, ${g.ips.length} ${groupNote}` : ''}.
          </div>
          {(() => {
            const rows = profile.filter(p => g.ips.includes(p.ip || p.key));
            if (!rows.length) return null;
            const first = Math.min(...rows.map(r => r.first)), last = Math.max(...rows.map(r => r.last));
            return <div style={{ fontSize: 12, color: t.muted, marginTop: 4 }}>First {fmt(first)} · last {fmt(last)}</div>;
          })()}
        </>
      )}
    </InfoCard>
  );
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
      {card(`Likely home ${noun}`, homeWork.home, 'nights')}
      {card(`Likely work ${noun}`, homeWork.work, 'weekdays during working hours')}
    </div>
  );
}

// Everything about the IP addresses in a set of events: likely home/work,
// optional VPN/Tor screening, a table with day/night/workday counts, and IPv6
// networks. `extraRows` adds IPs that aren't activity (e.g. a registration IP).
export function IpAnalysis({ events, tz, fmt, tzLabel, accountLabel = a => a, exportName = 'IP addresses' }) {
  const t = useT();
  const profile = useMemo(() => ipProfile(events, tz), [events, tz]);
  const homeWork = useMemo(() => homeAndWork(profile), [profile]);
  const networks = useMemo(() => ipv6Networks(profile), [profile]);
  const ips = useMemo(() => profile.map(p => p.ip), [profile]);
  const gate = useOnlineGate();
  const [screenOn, setScreenOn] = useState(false);
  const [screening, setScreening] = useState({});
  const [done, setDone] = useState(false);
  const onResults = React.useCallback((map, d) => { setScreening(map); setDone(d); }, []);
  const flagged = profile.filter(p => screeningFlagged(screening[p.ip.toLowerCase()]));
  const b = btnStyle(t);
  if (!profile.length) return <Note>No IP addresses with times in these records.</Note>;
  return (
    <>
      <HomeWorkCards homeWork={homeWork} fmt={fmt} profile={profile} />
      <Note>
        &ldquo;Likely home&rdquo; is the IP or IPv6 network used on the most different nights (22:00–06:00), and &ldquo;likely
        work&rdquo; the one used on the most weekdays 09:00–17:00, in the time zone chosen at the top. They&rsquo;re leads, not
        facts: confirm them with the ISP. Phone carrier IPs change often, so a home network usually shows up as Wi-Fi.
      </Note>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        {!screenOn ? <button style={b} onClick={() => gate.ask('ip-lists').then(ok => ok && setScreenOn(true))}>Screen these IPs for VPN, Tor, iCloud Private Relay and hosting</button>
          : <IpScreening ips={ips} onResults={onResults} />}
        {gate.note || (
          <span style={{ fontSize: 12, color: t.muted }}>
            Uses the same blocklists as the IP and Domain Intelligence tool, checked in your browser. The lists are downloaded the
            first time (or reused from that tool); the IP addresses themselves are never sent anywhere.
          </span>
        )}
      </div>
      {screenOn && done && (flagged.length
        ? <Note tone="warn">{plural(flagged.length, 'IP')} flagged as VPN, Tor, iCloud Private Relay, hosting or on abuse lists. Their location is not where the person was.</Note>
        : <Note>No VPN, Tor, Private Relay or hosting IPs found.</Note>)}
      <DataTable key={tz + screenOn} rows={profile} exportName={exportName} initialSort={{ key: 'days', dir: 'desc' }}
        columns={[
          { key: 'ip', label: 'IP address', mono: true, color: t.cyan },
          { key: 'v', label: 'Type', width: 60, text: r => (r.v6 ? 'IPv6' : 'IPv4') },
          { key: 'count', label: 'Records', width: 70, sort: r => r.count },
          { key: 'days', label: 'Days', width: 50, generated: true, title: 'Different days this IP was used', sort: r => r.days },
          { key: 'nights', label: 'Nights', width: 55, generated: true, title: 'Different nights (22:00–06:00) this IP was used', sort: r => r.nights },
          { key: 'workdays', label: 'Workdays', width: 70, generated: true, title: 'Different weekdays this IP was used 09:00–17:00', sort: r => r.workdays },
          { key: 'first', label: `First seen (${tzLabel})`, mono: true, text: r => fmt(r.first), sort: r => r.first },
          { key: 'last', label: 'Last seen', mono: true, text: r => fmt(r.last), sort: r => r.last },
          ...(screenOn ? [{ key: 'screen', label: 'Screening', generated: true, text: r => screeningLabel(screening[r.ip.toLowerCase()]),
            render: r => { const res = screening[r.ip.toLowerCase()]; return <span style={{ color: screeningFlagged(res) ? t.warn : t.muted }}>{screeningLabel(res)}</span>; } }] : []),
          { key: 'sources', label: 'Seen in', text: r => r.sources.join(', ') },
          { key: 'accounts', label: 'Accounts', text: r => r.accounts.map(accountLabel).join(', ') },
        ]} />
      {networks.length > 0 && (
        <>
          <Heading generated title="IPv6 networks" sub="IPv6 addresses grouped by their first 64 bits: addresses in one group came through the same home, office or carrier network." />
          <DataTable key={'n' + tz} rows={networks} exportName={`${exportName} IPv6 networks`} initialSort={{ key: 'count', dir: 'desc' }}
            columns={[
              { key: 'network', label: 'Network', mono: true, color: t.cyan },
              { key: 'addresses', label: 'Addresses', width: 80 },
              { key: 'count', label: 'Records', width: 80 },
              { key: 'nights', label: 'Nights', width: 60 },
              { key: 'first', label: `First (${tzLabel})`, mono: true, text: r => fmt(r.first), sort: r => r.first },
              { key: 'last', label: 'Last', mono: true, text: r => fmt(r.last), sort: r => r.last },
              { key: 'accounts', label: 'Accounts', text: r => r.accounts.map(accountLabel).join(', ') },
            ]} />
        </>
      )}
    </>
  );
}

// ─── Contacts ────────────────────────────────────────────────────────────────

// Ranked by different days in contact, with a "first seen on/after" filter and,
// for the selected contact, a bar per day.
export function ContactsView({ contacts, fmt, tzLabel, accountLabel = a => a, label = 'Contact', note, showDirection, nameOf, show = h => h }) {
  const t = useT();
  const [since, setSince] = useState('');
  const [hideOwn, setHideOwn] = useState(true);
  const [selected, setSelected] = useState(null);
  const sinceT = since ? Date.parse(since + 'T00:00:00Z') : null;
  const rows = contacts.filter(c => (!hideOwn || !c.own) && (sinceT == null || c.first >= sinceT));
  const sel = rows.find(c => c.key === selected);
  const s = selectStyle(t);
  const maxDay = sel ? Math.max(1, ...sel.perDay.map(([, n]) => n)) : 1;
  const hasOwn = contacts.some(c => c.own);
  return (
    <>
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', fontSize: 12.5, color: t.muted }}>
        <label>First contact on or after{' '}
          <input type="date" value={since} onChange={e => setSince(e.target.value)} style={{ ...s, colorScheme: scheme(t) }} />
        </label>
        {since && <button style={btnStyle(t)} onClick={() => setSince('')}>Clear date</button>}
        {hasOwn && (
          <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={hideOwn} onChange={e => setHideOwn(e.target.checked)} /> Hide the account&rsquo;s own numbers and emails
          </label>
        )}
      </div>
      <Note>
        <span style={{ color: t.warn }}>✱</span> Ranked by <strong style={{ color: t.text }}>days</strong>: how many different days
        there was contact. Bursts inflate raw counts; someone in contact on many separate days is much more likely to matter.
        Use the date filter to find people who first appear after a given day (for example, after the incident).
        {note ? ` ${note}` : ''} Click a row to see that contact day by day.
      </Note>
      <DataTable key={tzLabel + since + hideOwn} rows={rows} exportName="contacts by day" initialSort={{ key: 'days', dir: 'desc' }}
        onRowClick={r => setSelected(r.key === selected ? null : r.key)} rowKey={r => r.key} selectedKey={selected}
        emptyText="No contacts match."
        columns={[
          { key: 'handle', label, mono: true, color: t.cyan, text: r => (nameOf && nameOf(r) ? `${show(r.handle)} · ${nameOf(r)}` : show(r.handle)) },
          { key: 'days', label: 'Days', width: 50, generated: true, title: 'Different days in contact', sort: r => r.days },
          { key: 'count', label: 'Records', width: 70, sort: r => r.count },
          ...(showDirection ? [{ key: 'dir', label: 'In / out', width: 70, text: r => `${r.in} / ${r.out}` }] : []),
          { key: 'first', label: `First (${tzLabel})`, mono: true, text: r => fmt(r.first), sort: r => r.first },
          { key: 'last', label: 'Last', mono: true, text: r => fmt(r.last), sort: r => r.last },
          { key: 'services', label: 'Type', text: r => r.services.join(', ') },
          { key: 'accounts', label: 'Accounts', text: r => r.accounts.map(accountLabel).join(', ') },
          { key: 'notes', label: 'Notes', generated: true, text: r => [r.own && 'own', r.accounts.length > 1 && 'shared by accounts'].filter(Boolean).join(', ') },
        ]} />
      {sel && (
        <InfoCard title={<>{show(sel.handle)} · {plural(sel.count, 'record')} on {plural(sel.days, 'day')}<span style={{ color: t.warn }}> ✱</span></>}
          action={<button style={btnStyle(t)} onClick={() => setSelected(null)}>Close</button>}>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 80, overflowX: 'auto' }}>
            {fillDays(sel.perDay.map(([day, count]) => ({ day, count }))).map(d => (
              <div key={d.day} title={`${d.day} · ${d.count}`} style={{ flex: '1 0 5px', minWidth: 5, height: d.count ? Math.max(3, Math.round(d.count / maxDay * 80)) : 2, background: d.count ? t.accent : t.heatEmpty, borderRadius: 1 }} />
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: t.muted, marginTop: 6 }}>
            <span>{fmt(sel.first)}</span><span>Days in {tzLabel}</span><span>{fmt(sel.last)}</span>
          </div>
        </InfoCard>
      )}
    </>
  );
}

// ─── Links between accounts ──────────────────────────────────────────────────

export function LinksView({ links, accountLabel = a => a, targetCount, what = 'accounts in these records', explain }) {
  const strength = l => (l.devices.length || (l.identifiers || []).length ? ['Strong', 'warn'] : l.networks.length || l.ips.length >= 5 ? ['Likely', 'warn'] : ['Possible', '']);
  const sample = (list, n = 12) => (list.length > n ? `${list.slice(0, n).join(', ')} … and ${list.length - n} more` : list.join(', '));
  return (
    <>
      <Heading title="Links between accounts" sub={`The ${what} that share devices, networks, IP addresses or contacts.`} />
      <GeneratedBadge />
      <Explain>
        A shared device, email address or phone number is the strongest sign two accounts are the same person or used on the same phone. A shared home network
        or several shared IPs suggests the same household or workplace. A single shared IP can be a coincidence: phone carriers
        and public Wi-Fi put many people behind the same address. Shared contacts point to common associates.
        {explain ? ` ${explain}` : ''}
      </Explain>
      {targetCount < 2 ? <Note>There is only one account here, so there is nothing to compare.</Note>
        : !links.length ? <Note>No shared devices, networks, IPs or contacts between the accounts.</Note>
          : links.map(l => {
            const [label, tone] = strength(l);
            const rows = [['Shared devices', l.devices], ['Shared emails / phones', l.identifiers], ['Shared IPv6 networks', l.networks], ['Shared IPs', l.ips], ['Shared contacts', l.contacts]].filter(([, v]) => v && v.length);
            return (
              <InfoCard key={l.a + l.b} title={`${accountLabel(l.a)}  ↔  ${accountLabel(l.b)}`} action={<Chip tone={tone}>{label} link</Chip>}>
                {rows.map(([k, v], i) => <InfoRow key={k} first={i === 0} label={`${k} (${v.length})`} value={sample(v)} mono />)}
              </InfoCard>
            );
          })}
    </>
  );
}

// ─── Account / security events ───────────────────────────────────────────────

export const ipHistory = e => {
  if (!e.ip || /created|signed up/i.test(e.type)) return '';
  if (e.newIp) return 'NEW IP';
  return e.seen ? `seen ${e.seen}x` : 'not in activity logs';
};

export function SecurityView({ rows, fmt, tzLabel, accountLabel = a => a, title = 'Account security events', sub, explain, emptyText = 'No account events in these records.' }) {
  const t = useT();
  const flagged = rows.filter(r => r.newIp).length;
  return (
    <>
      <Heading title={title} sub={sub} />
      <GeneratedBadge />
      <Explain>
        {explain ? `${explain} ` : ''}An event from an IP address the account hadn&rsquo;t used before that day (flagged{' '}
        <Chip tone="warn">new IP</Chip>: no activity from it more than 24 hours earlier) is worth a closer look: it can mean someone
        else got into the account, a new device or network, or the user covering their tracks. &ldquo;Seen&rdquo; is how many
        activity records in these records use that IP.
      </Explain>
      {flagged > 0 && <Note tone="warn">{plural(flagged, 'event')} came from an IP the account hadn&rsquo;t used before.</Note>}
      <DataTable key={tzLabel} rows={rows} exportName="account events" initialSort={{ key: 't', dir: 'asc' }} emptyText={emptyText}
        columns={[
          { key: 't', label: `Time (${tzLabel})`, mono: true, text: r => fmt(r.t), sort: r => r.t, width: 190 },
          { key: 'account', label: 'Account', text: r => accountLabel(r.account) },
          { key: 'type', label: 'Event' },
          { key: 'detail', label: 'Details', maxWidth: 520 },
          { key: 'ip', label: 'IP', mono: true, color: t.cyan,
            render: r => (r.ipInferred ? <span title={`No IP was logged with this event; this is the IP of the nearest activity (within 2 hours). ${GENERATED}`}>{r.ip} <span style={{ color: t.warn }}>≈✱</span></span> : r.ip) },
          { key: 'flag', label: 'IP history', generated: true, text: r => ipHistory(r), render: r => (r.newIp ? <Chip tone="warn">new IP</Chip> : ipHistory(r)) },
        ]} />
    </>
  );
}

// ─── Case summary PDF ────────────────────────────────────────────────────────

export function CaseSummaryView({ onDownload, busy, scope, counts, explain }) {
  const t = useT();
  return (
    <>
      <Heading title="Case summary" sub={`A printable PDF of the key findings for ${scope}.`} />
      <GeneratedBadge />
      <Explain>
        {explain || 'One document for the case file with the main findings from the analysis pages.'} It follows the time zone
        (and account) chosen on the page, and every page says it&rsquo;s a review aid and that the original returns must be checked.
      </Explain>
      <InfoCard title="What goes in">
        {counts.map(([k, v], i) => <InfoRow key={k} label={k} value={v} first={i === 0} />)}
      </InfoCard>
      <div>
        <button style={{ ...btnStyle(t), fontSize: 14, padding: '9px 18px', background: t.accent, borderColor: t.accent, color: '#fff' }} disabled={busy} onClick={onDownload}>
          {busy ? 'Building PDF…' : 'Download case summary (PDF)'}
        </button>
      </div>
    </>
  );
}

async function loadPdfMake() {
  const pdfMake = (await import('pdfmake/build/pdfmake')).default;
  const fonts = (await import('pdfmake/build/vfs_fonts')).default;
  pdfMake.vfs = fonts.vfs || fonts.pdfMake?.vfs || fonts;
  return pdfMake;
}

export async function downloadPdf(doc, filename) {
  const pdfMake = await loadPdfMake();
  pdfMake.createPdf(doc).download(filename);
}

// A case-summary document. sections: [{ heading, small?, kv?: [[k, v]], lines?: [..],
// table?: { headers, rows, widths? }, heatmap?: patternOfLife result }]. The PDF font
// has no ✱, so the PDF marks generated content with a plain asterisk.
// The PDF font (Roboto) has no arrows, ✱ or ≈, so those become plain text.
const pdfText = v => String(v ?? '').replace(/→/g, '->').replace(/↔/g, 'and').replace(/✱/g, '*').replace(/≈/g, '~');

export function summaryDoc({ title, scope, fmt, tzLabel, source = 'the provider', sections }) {
  const H = text => ({ text, fontSize: 13, bold: true, color: '#0f172a', margin: [0, 14, 0, 6] });
  const small = text => ({ text, fontSize: 8.5, color: '#64748b', margin: [0, 0, 0, 4] });
  const table = (headers, rows, widths) => (rows.length ? {
    table: { headerRows: 1, widths: widths || headers.map(() => '*'), body: [headers.map(h => ({ text: h, bold: true, fontSize: 8.5, fillColor: '#e2e8f0' })), ...rows.map(r => r.map(c => ({ text: pdfText(c), fontSize: 8.5 })))] },
    layout: 'lightHorizontalLines', margin: [0, 0, 0, 6],
  } : { text: 'None.', fontSize: 9, color: '#64748b' });
  const heat = pol => {
    const max = pol.max;
    return {
      table: {
        widths: [24, ...new Array(24).fill('*')],
        body: [
          ['', ...Array.from({ length: 24 }, (_, h) => ({ text: h % 3 === 0 ? String(h) : '', fontSize: 6, alignment: 'center' }))],
          ...ORDER.map(wd => [{ text: DAYS[wd], fontSize: 7 }, ...pol.grid[wd].map(n => ({
            text: n ? String(n) : '', fontSize: 5.5, alignment: 'center', color: n / max > 0.55 ? '#ffffff' : '#1e3a8a',
            fillColor: n ? `#${[59, 130, 246].map(c => Math.round(255 - (255 - c) * (0.15 + 0.85 * n / max)).toString(16).padStart(2, '0')).join('')}` : '#f8fafc',
          }))]),
        ],
      },
      layout: { hLineWidth: () => 0.5, vLineWidth: () => 0.5, hLineColor: () => '#ffffff', vLineColor: () => '#ffffff' },
    };
  };
  const content = [
    { text: title, fontSize: 18, bold: true },
    { text: `${scope} · Generated ${fmt(Date.now())} · Times in ${tzLabel}`, fontSize: 9, color: '#64748b', margin: [0, 2, 0, 8] },
    { text: `* ${GENERATED} Everything in this summary was compiled and calculated by LE Tech Tools from the records ${source} produced.`, fontSize: 9, bold: true, color: '#b45309', margin: [0, 0, 0, 4] },
    { text: `Review aid only. Always refer to the actual returns from ${source}. This summary does not replace the original data, and anything relied on in a report, affidavit or court must be checked against the original files. Items marked "likely" are inferences with the evidence shown, not facts.`, fontSize: 9, color: '#92400e', margin: [0, 0, 0, 6] },
  ];
  for (const s of sections) {
    if (s.heading) content.push(H(s.heading));
    if (s.small) content.push(small(s.small));
    if (s.kv) content.push({ table: { widths: [110, '*'], body: s.kv.filter(([, v]) => v).map(([k, v]) => [{ text: k, fontSize: 8.5, color: '#64748b' }, { text: pdfText(v), fontSize: 8.5 }]) }, layout: 'noBorders', margin: [0, 0, 0, 8] });
    if (s.lines) s.lines.forEach(l => content.push({ text: pdfText(l), fontSize: 9, margin: [0, 0, 0, 3] }));
    if (s.table) content.push(table(s.table.headers, s.table.rows, s.table.widths));
    if (s.heatmap) content.push(heat(s.heatmap));
  }
  return {
    pageMargins: [36, 40, 36, 40], defaultStyle: { font: 'Roboto', fontSize: 9 }, content,
    footer: (page, pages) => ({ text: `* ${GENERATED} Review aid only: refer to the original returns. · Page ${page} of ${pages}`, fontSize: 7, color: '#94a3b8', alignment: 'center', margin: [0, 10, 0, 0] }),
  };
}

// Common summary sections every tool can include.
export function standardSections({ events, tz, fmt, contacts = [], contactLabel = 'Contact', screen = () => 'Not screened' }) {
  const pol = patternOfLife(events, tz);
  const gaps = activityGaps(events, 12).sort((a, b) => b.hours - a.hours);
  const prof = ipProfile(events, tz);
  const hw = homeAndWork(prof);
  const out = [];
  if (prof.length) {
    out.push({ heading: 'Likely home and work networks', lines: [['Likely home', hw.home], ['Likely work', hw.work]].map(([k, g]) =>
      (g ? `${k}: ${g.label} (seen on ${g.nights} nights and ${g.workdays} weekday working days, ${g.count} records)` : `${k}: none seen on 3 or more days.`)) });
    out.push({ heading: 'Top IP addresses', table: { headers: ['IP', 'Records', 'Days', 'Nights', 'First', 'Last', 'Screening'],
      rows: [...prof].sort((a, b) => b.days - a.days || b.count - a.count).slice(0, 15).map(r => [r.ip, r.count, r.days, r.nights, fmt(r.first), fmt(r.last), screen(r.ip)]),
      widths: ['*', 38, 30, 34, 85, 85, 80] } });
  }
  if (contacts.length) {
    out.push({ heading: `Top contacts`, small: 'Ranked by the number of different days in contact.', table: { headers: [contactLabel, 'Days', 'Records', 'First', 'Last'],
      rows: [...contacts].filter(c => !c.own).sort((a, b) => b.days - a.days || b.count - a.count).slice(0, 15).map(c => [c.handle, c.days, c.count, fmt(c.first), fmt(c.last)]),
      widths: ['*', 30, 45, 90, 90] } });
  }
  if (pol.total) {
    out.push({ heading: 'Pattern of life', small: 'Activity records per weekday and hour. Darker = busier.', heatmap: pol });
    out.push({ heading: 'Longest activity gaps', table: { headers: ['From', 'To', 'Length'], rows: gaps.slice(0, 10).map(g => [fmt(g.from), fmt(g.to), hoursText(g.hours)]) } });
  }
  return out;
}

// ─── Shell ───────────────────────────────────────────────────────────────────

// A side menu of analysis pages next to a scrolling content column, for tools
// that don't already have a sidebar. items: [{ id, label, badge? }].
export function AnalysisShell({ items, active, onSelect, header, children }) {
  const t = useT();
  return (
    <div style={{ flex: 1, display: 'flex', overflow: 'hidden', background: t.bg, color: t.text }}>
      <div style={{ width: 230, flexShrink: 0, borderRight: `1px solid ${t.border}`, background: t.surface, overflowY: 'auto', padding: '10px 0' }}>
        {header}
        <div style={{ padding: '6px 16px 6px', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: t.muted }}>
          Analysis <span title={GENERATED} style={{ color: t.warn }}>✱</span>
        </div>
        {items.map(i => <NavBtn key={i.id} label={i.label} badge={i.badge} active={active === i.id} onClick={() => onSelect(i.id)} />)}
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '20px 24px 40px', maxWidth: 1300 }}>{children}</div>
      </div>
    </div>
  );
}
